import {
    FLIGHT_PACKAGE,
    ABROAD_HOTEL,
    HOTEL_FLIGHT_PACKAGE,
    DOMESTIC_FLIGHT,
    DOMESTIC_HOTEL,
    DOMESTIC_PACKAGE,
    ORG_TOUR,
    FLY_DRIVE
} from '@lts-web/types/ModuleTypes';
import SearchModuleDataSchema from "@lts-web/schemas/SearchModuleDataSchema";

export const conventions = {
    LMC: [
        FLIGHT_PACKAGE.description,
        ABROAD_HOTEL.description,
        HOTEL_FLIGHT_PACKAGE.description,
        DOMESTIC_FLIGHT.description,
        DOMESTIC_HOTEL.description,
        DOMESTIC_PACKAGE.description,
        ORG_TOUR.description,
        FLY_DRIVE.description
    ],
    NMC: [
        'abroadFlight',
        'abroadHotel',
        'abroadPackage',
        'domesticFlight',
        'domesticHotel',
        'domesticPackage',
        'orgTour',
        'searchCar'
    ],
    OMC: [
        'broadnoHotel',
        'broadonlyHotel',
        'broadwithHotel',
        'israelnoHotel',
        'israelonlyHotel',
        'israelwithHotel',
        'orgTour',
        'searchCar'
    ],
    CMC: [
        'searchFlight',
        'searchHotel',
        'searchPackage',
        'searchIsraelnoHotel',
        'israelonlyHotel',
        'searchIsraelwithHotel',
        'searchOrgTour',
        'searchCar'
    ]
};

class SearchModuleData {
    constructor(data = {}) {
        this._data = Object.freeze(SearchModuleDataSchema(data));

        if (!SearchModuleData.hasModuleName(this.getName(), this.getConvention()))
            throw new Error(`Module name is invalid. Check out the NMC (New Module Convention) names, located in "@lts-web/data-objects/SearchModuleData.js"`);

        Reflect.defineProperty(this, '_data', {
            writable: false,
            configurable: false,
            enumerable: false
        });
    }

    /**
     * Check if the provided moduleName exists as part of the provided conventionName
     *
     * @param moduleName
     * @param conventionName
     *
     * @returns {boolean}
     */
    static hasModuleName (moduleName, conventionName) {
        const keys = conventions[conventionName] || {};
        return keys.indexOf(moduleName) > -1;
    }


    /**
     * Get searchModule data
     * @return {Readonly<SearchModuleDataSchema>}
     */
    getData() {
        return this._data;
    }

    /**
     * Get name
     * @returns {string}
     */
    getName() {
        return this._data.name;
    }

    /**
     * Get convention
     * @returns {string}
     */
    getConvention() {
        return this._data.convention;
    }

    /**
     * Get module type
     * @returns {String}
     */
    getType() {
        return this._data.type;
    }

    /**
     * Get number of adults
     * @returns {string}
     */
    getDefaultPax() {
        return this._data.numOfAdults;
    }

    /**
     * Check if domestic voucher
     * @returns {boolean}
     */
    isDomesticVoucher() {
        return this._data.isDomesticVoucher === true;
    }

    /**
     * Check if Eilat resident
     * @returns {boolean}
     */
    isEilatResident() {
        return this._data.isEilatResident === true;
    }

    /**
     * Check if Otef resident
     * @returns {boolean}
     */
    isOtefResident() {
        return this._data.isOtefResident === true;
    }

    /**
     * Check if Soldier discount
     * @returns {boolean}
     */
    isSoldier() {
        return this._data.isSoldier === true;
    }

    isPoliceman() {
        return this._data.isPoliceman === true;
    }

    /**
     * Check if route type oneWay is supported
     * @returns {boolean}
     */
    isSupportOneWay() {
        return this._data.isSupportOneWay === true;
    }

    /**
     * Check if should show infants
     * @returns {boolean}
     */
    isShowInfants() {
        return this._data.isShowInfants === true;
    }

    /**
     * Check if sameDayReturn is supported
     * @returns {boolean}
     */
    sameDayReturn() {
        return this._data.sameDayReturn;
    }

    /**
     * Check if should show TwoWay
     * @returns {boolean}
     */
    showTwoWay() {
        return this._data.showTwoWay;
    }

    /**
     * Check if budget is supported
     * @returns {boolean}
     */
    isSupportBudget() {
        return this._data.supportBudget === true;
    }

    /**
     * Check if should disable the Next Button when clearing a calendar
     * @returns {boolean}
     */
    disableNextOnClear() {
        return this._data.disableNextOnClear;
    }

    /**
     * Check if flex search is supported
     * @returns {boolean}
     */
    isSupportFlexSearch() {
        return this._data.supportFlexSearch === true;
    }

    /**
     * Check if from abroad flight is supported
     * @returns {boolean}
     */
    isSupportFromAbroadFlight() {
        return this._data.supportFromAbroadFlight === true;
    }

    /**
     * Check if multi destination is supported
     * @return {boolean}
     */
    isSupportMultiDestination() {
        return this._data.supportMultiDestination === true;
    }

    /**
     * Check if it is domestic module
     * @returns {boolean}
     */
    isDomesticModule() {
        return this._data.isDomesticModule === true;
    }

    /**
     * Check if it is only flight module
     * @returns {boolean}
     */
    isOnlyFlightModule() {
        return this._data.onlyFlightModule === true;
    }

    /**
     * Check if it is only hotel module
     * @returns {boolean}
     */
    isOnlyHotelModule() {
        return this._data.onlyHotelModule === true;
    }

    /**
     * Check if specific dates is a requirement
     * @returns {boolean}
     */
    isSpecificDatesRequired() {
        return this._data.specificDatesRequired === true;
    }

    /**
     * Check if defined dates is a requirement
     * @returns {boolean}
     */
    isDefinedDatesRequired() {
        return this._data.definedDatesRequired === true;
    }

    /**
     * Check if a defined destination is a requirement
     * @returns {boolean}
     */
    isDefinedDestRequired() {
        return this._data.definedDestRequired === true;
    }

    /**
     * Check if a dates painting is supported
     * @returns {boolean}
     */
    isDatesPaintingSupported() {
        return this._data.datesPaintingSupported === true;
    }

    /**
     * Check if autoComplete is supported
     * @returns {boolean}
     */
    isAutoCompleteSupported() {
        return this._data.autoCompleteSupported === true;
    }

    /**
     * Is subject supported
     * @returns {boolean}
     */
    isSubjectSupported() {
        return this._data.subjectSupported === true;
    }

    /**
     * Check if module supports Open Calendar;
     * Open Calendar means that unmarked dates are not disabled but are "open".
     * @return {boolean}
     */
    isOpenCalendarSupported() {
        return this._data.openCalendarSupported === true;
    }

    /**
     * Get calendar type
     * @return {string}
     */
    getCalendarType() {
        return this._data.calendarType;
    }

    /**
     * Check if Summer Winter Supported
     * @returns {boolean}
     */
    isSummerWinter() {
        return this._data.isSummerWinter === true;
    }
}

export default SearchModuleData;